import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { percentToTimestamp, useTimelineDrag } from '../../../hooks/useTimelineDrag';
import { MIN_POLL_DURATION, PollTimelineEvent, Timeline } from '../../../parser';

export const PollsTrack = ({
    timeline,
    duration,
    isActive,
    onClick
}: {
    timeline: Timeline;
    duration: Duration;
    isActive: boolean;
    onClick: (event: PollTimelineEvent) => void;
}) => {
    const [polls, setPolls] = useState<PollTimelineEvent[]>([]);

    useEffect(() => {
        if (!timeline?.polls || duration.toMillis() === 0) {
            setPolls([]);
            return;
        }

        setPolls(timeline.polls);
    }, [timeline?.polls, duration]);

    const sortedPolls = () => [...timeline.polls.sort((a, b) => a.timestamp.toMillis() - b.timestamp.toMillis())];

    const markChanged = (poll: PollTimelineEvent) => {
        poll.changed = true;
        timeline.changed = true;
        timeline.polls = [...timeline.polls];
    };

    const drag = useTimelineDrag<PollTimelineEvent>((poll, position) => {
        // Video time works in seconds, so we need to round the timestamp to the nearest second
        poll.timestamp = percentToTimestamp(duration, position);
        // Check if there are two polls with the same timestamp
        const duplicate = timeline.polls.find((p) => p.timestamp.equals(poll.timestamp) && p !== poll);
        if (!!duplicate) {
            if (poll.timestamp.as('seconds') > 0) {
                poll.timestamp = poll.timestamp.minus({ seconds: 1 });
            } else {
                poll.timestamp = poll.timestamp.plus({ seconds: 1 });
            }
        }
        setPolls(sortedPolls());
    }, markChanged);

    const resize = useTimelineDrag<PollTimelineEvent>((poll, position) => {
        const timelinePosition = percentToTimestamp(duration, position);
        let newDuration = timelinePosition.minus(poll.timestamp).as('seconds');
        if (newDuration < MIN_POLL_DURATION.as('seconds')) {
            newDuration = MIN_POLL_DURATION.as('seconds');
        }
        poll.duration = Duration.fromObject({ seconds: newDuration });
        setPolls(sortedPolls());
    }, markChanged);

    const onDragStart = (e: React.MouseEvent<HTMLButtonElement>, poll: PollTimelineEvent) =>
        drag.begin(e, poll, (poll.timestamp.toMillis() / duration.toMillis()) * 100);

    const onResizeStart = (e: React.MouseEvent<HTMLButtonElement>, poll: PollTimelineEvent) =>
        resize.begin(e, poll, (poll.timestamp.plus(poll.duration).toMillis() / duration.toMillis()) * 100);

    return (
        <div className={`track track-polls ${isActive ? 'track-active' : ''} row-start-5`}>
            {polls.map((poll, index) => {
                const width = (poll.duration.toMillis() / duration.toMillis()) * 100;
                return (
                    <div
                        key={index}
                        className={`poll ${poll.changed ? 'changed' : ''}`}
                        style={{ left: `${(poll.timestamp.toMillis() / duration.toMillis()) * 100}%`, width: `${width}%` }}
                        onClick={() => onClick(poll)}
                    >
                        <button onMouseDown={(event) => onDragStart(event, poll)}></button>
                        <span>{poll.poll.question}</span>
                        <button onMouseDown={(event) => onResizeStart(event, poll)}></button>
                    </div>
                );
            })}
        </div>
    );
};
